import type {
  RuleGroupType,
  RuleType,
  ValidationMap,
  ValidationResult,
} from "react-querybuilder";
import {
  isCountRule,
  isCountTargetField,
  type CountAwareRule,
} from "./countCondition";
import {
  isFieldPlaceholderValue,
  parseEnhancedTaggingConditionValue,
} from "./enhancedTaggingConditionValue";

export function isEnhancedTaggingRuleComplete(
  rule: CountAwareRule,
): ValidationResult {
  if (isCountRule(rule)) {
    const reasons: string[] = [];
    if (!isCountTargetField(String(rule.field ?? ""))) {
      reasons.push("field");
    }
    return { valid: reasons.length === 0, reasons };
  }

  const reasons: string[] = [];

  if (isFieldPlaceholderValue(String(rule.field ?? ""))) {
    reasons.push("field");
  }
  if (!String(rule.operator ?? "").trim()) {
    reasons.push("operator");
  }

  const value = parseEnhancedTaggingConditionValue(rule.value);
  if (!value.allValues && value.conceptValues.length === 0) {
    reasons.push("value");
  }

  return { valid: reasons.length === 0, reasons };
}

export function countEnhancedTaggingConditionRules(
  query: RuleGroupType,
): number {
  let count = 0;

  const walk = (group: RuleGroupType) => {
    for (const item of group.rules) {
      if (item && typeof item === "object" && "rules" in item) {
        walk(item as RuleGroupType);
      } else {
        count += 1;
      }
    }
  };

  walk(query);
  return count;
}

/**
 * True when any leaf rule has a selected dimension or value — used to warn
 * before resetting conditions on Object Type / Sub-Type change.
 */
export function hasEnhancedTaggingConditionData(
  query: RuleGroupType,
): boolean {
  const walk = (group: RuleGroupType): boolean => {
    for (const item of group.rules) {
      if (item && typeof item === "object" && "rules" in item) {
        if (walk(item as RuleGroupType)) return true;
        continue;
      }
      const rule = item as RuleType;
      if (isCountRule(rule)) {
        if (isCountTargetField(String(rule.field ?? ""))) return true;
        continue;
      }
      if (!isFieldPlaceholderValue(String(rule.field ?? ""))) {
        return true;
      }
      const value = parseEnhancedTaggingConditionValue(rule.value);
      if (value.allValues || value.conceptValues.length > 0) {
        return true;
      }
    }
    return false;
  };

  return walk(query);
}

export type EnhancedTaggingConditionValidation = {
  valid: boolean;
  ruleCount: number;
  validationMap: ValidationMap;
  message: string | null;
};

export function validateEnhancedTaggingConditionQuery(
  query: RuleGroupType,
): EnhancedTaggingConditionValidation {
  const validationMap: ValidationMap = {};
  let ruleCount = 0;

  const walk = (group: RuleGroupType) => {
    for (const item of group.rules) {
      if (item && typeof item === "object" && "rules" in item) {
        walk(item as RuleGroupType);
        continue;
      }

      ruleCount += 1;
      const rule = item as RuleType;
      const result = isEnhancedTaggingRuleComplete(rule);
      if (rule.id) {
        validationMap[rule.id] = result;
      } else if (!result.valid) {
        validationMap[`__anonymous_${ruleCount}`] = result;
      }
    }
  };

  walk(query);

  if (ruleCount === 0) {
    return {
      valid: false,
      ruleCount: 0,
      validationMap,
      message: "Please add at least one condition.",
    };
  }

  const hasInvalidRule = Object.values(validationMap).some((result) => {
    if (typeof result === "boolean") return !result;
    return !result.valid;
  });

  return {
    valid: !hasInvalidRule,
    ruleCount,
    validationMap,
    message: hasInvalidRule
      ? "Please complete all condition fields."
      : null,
  };
}

export function hasValidationReason(
  validation: boolean | ValidationResult | undefined,
  reason: string,
): boolean {
  if (validation == null || validation === true) return false;
  if (validation === false) return true;
  if (validation.valid) return false;
  if (!Array.isArray(validation.reasons) || validation.reasons.length === 0) {
    return true;
  }
  return validation.reasons.includes(reason);
}
